import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';

import { MAX_AMOUNT_LIMIT, MIN_AMOUNT_LIMIT } from '../../constants/numberLimit';
import { safeParseFloat } from '../../lib/helpers';
import { currencyFormattedNumber } from '../../lib/numberFormatter';
import { updateValue } from '../../utils/utils';
import Button from '../Button';
import CombinedTextField from '../CombinedTextField';
import InputField from '../InputField';
import PendingAmountLabel from '../PendingAmountLabel/PendingAmountLabel';
import Select from '../Select';

const LoanAmountInput = ({
  currency,
  handleSubmit,
  amount,
  onAmountChange,
  personalSavings,
  onPersonalSavingsChange,
  relativesContribution,
  onRelativesContributionChange,
  minLoanAmount,
  maxLoanAmount,
  otherFunds,
  requiredAmount,
  zeroAmountAllowed,
}) => {
  const [error, setError] = useState(null);
  const currencies = [{ label: currency, value: currency }];

  const onSubmit = event => {
    event.preventDefault();

    if (isInvalid) return;

    handleSubmit();
  };

  const loanHeader = useMemo(() => {
    const formattedMinLoanAmount = currencyFormattedNumber(currency, minLoanAmount);
    const formattedMaxLoanAmount = currencyFormattedNumber(currency, maxLoanAmount);

    return `How much do you want to borrow? (between ${formattedMinLoanAmount} and ${formattedMaxLoanAmount})`;
  }, [minLoanAmount, maxLoanAmount, currency]);

  const displayLoanAmountInput = () => {
    if (zeroAmountAllowed) return minLoanAmount <= maxLoanAmount;
    return true;
  };
  const pendingAmount = useMemo(() => {
    const parsedAmount = safeParseFloat(amount, 0);
    const parsedPersonalSavings = safeParseFloat(personalSavings, 0);
    const parsedRelativesContribution = safeParseFloat(relativesContribution, 0);

    const pending =
      requiredAmount - parsedAmount - parsedPersonalSavings - parsedRelativesContribution;
    return pending < 0 ? 0 : pending;
  }, [requiredAmount, amount, personalSavings, relativesContribution]);

  const isInvalid = useMemo(() => {
    const safeAmount = parseFloat(amount) || 0;

    const parsedPersonalSavings = safeParseFloat(personalSavings, 0);
    const parsedRelativesContribution = safeParseFloat(relativesContribution, 0);

    if (parsedPersonalSavings < 0) {
      setError(`Personal savings cannot be negative. Please check this amount.`);
      return true;
    }

    if (parsedRelativesContribution < 0) {
      setError(
        `Contributions from friends and family cannot be negative. Please check this amount.`,
      );
      return true;
    }

    if (safeAmount > maxLoanAmount) {
      setError(
        `Maximum loan size is ${currencyFormattedNumber(
          currency,
          maxLoanAmount,
        )}. Please check your loan amount.`,
      );
      return true;
    }

    if (safeAmount === 0 && zeroAmountAllowed && pendingAmount <= 0) {
      setError(null);
      return false;
    }
    if (safeAmount < minLoanAmount) {
      setError(
        `Minimum loan size is ${currencyFormattedNumber(
          currency,
          minLoanAmount,
        )}. Please check your loan amount.`,
      );
      return true;
    }

    if (pendingAmount > 0) {
      setError(null);
      return true;
    }

    setError(null);
    return false;
  }, [
    minLoanAmount,
    maxLoanAmount,
    amount,
    currency,
    personalSavings,
    relativesContribution,
    pendingAmount,
    zeroAmountAllowed,
  ]);

  const inputErrorClass = isInvalid ? 'has-error' : '';

  return (
    <form onSubmit={onSubmit}>
      <div className="row">
        <div className="col col--12/16-large">
          {displayLoanAmountInput() && (
            <div className="row-spacer">
              <p className="text-field__input-header">{loanHeader}</p>
              <CombinedTextField>
                <Select
                  id="loan-currency"
                  value={currency}
                  onChange={() => {}}
                  options={currencies}
                  disabled
                />

                <InputField
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  id="loan-amount"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={e => updateValue(e, onAmountChange, MAX_AMOUNT_LIMIT)}
                  min={MIN_AMOUNT_LIMIT}
                  max={MAX_AMOUNT_LIMIT}
                  className={inputErrorClass}
                />
              </CombinedTextField>
            </div>
          )}
          <div className="row-spacer">
            <p className="text-field__input-header">Personal savings you plan to use</p>
            <CombinedTextField>
              <Select
                id="personal-savings-currency"
                value={currency}
                onChange={() => {}}
                options={currencies}
                disabled
              />

              <InputField
                type="text"
                inputMode="numeric"
                pattern="\d*"
                id="personal-savings-amount"
                placeholder="Enter amount"
                value={personalSavings}
                onChange={e => updateValue(e, onPersonalSavingsChange, MAX_AMOUNT_LIMIT)}
                min={MIN_AMOUNT_LIMIT}
                max={MAX_AMOUNT_LIMIT}
              />
            </CombinedTextField>
          </div>

          <div className="row-spacer">
            <p className="text-field__input-header">Contributions from friends and family</p>
            <CombinedTextField>
              <Select
                id="relatives-contribution-currency"
                value={currency}
                onChange={() => {}}
                options={currencies}
                disabled
              />

              <InputField
                type="text"
                inputMode="numeric"
                pattern="\d*"
                id="relatives-amount"
                placeholder="Enter amount"
                value={relativesContribution}
                onChange={e => updateValue(e, onRelativesContributionChange, MAX_AMOUNT_LIMIT)}
                min={MIN_AMOUNT_LIMIT}
                max={MAX_AMOUNT_LIMIT}
              />
            </CombinedTextField>
          </div>
        </div>
      </div>

      {isInvalid && <div className="row-spacer text-error">{error}</div>}

      <PendingAmountLabel
        currency={currency}
        otherFunds={otherFunds}
        pendingAmount={pendingAmount}
        errorMessage={error}
      />

      <div className="row">
        <div className="col col--auto">
          <Button type="submit" isDisabled={isInvalid}>
            Next
          </Button>
        </div>
      </div>
    </form>
  );
};

LoanAmountInput.propTypes = {
  currency: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  amount: PropTypes.string.isRequired,
  onAmountChange: PropTypes.func.isRequired,
  personalSavings: PropTypes.string.isRequired,
  onPersonalSavingsChange: PropTypes.func.isRequired,
  relativesContribution: PropTypes.string.isRequired,
  onRelativesContributionChange: PropTypes.func.isRequired,
  minLoanAmount: PropTypes.number.isRequired,
  maxLoanAmount: PropTypes.number.isRequired,
  otherFunds: PropTypes.number.isRequired,
  requiredAmount: PropTypes.number.isRequired,
  zeroAmountAllowed: PropTypes.bool,
};

LoanAmountInput.defaultProps = {
  zeroAmountAllowed: false,
};

export default LoanAmountInput;
